import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser, AdminAuthContextType } from '../types';
import { adminAuthAPI } from '../services/api';
import { toast } from 'react-toastify';

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminAuthToken');
    const userData = localStorage.getItem('adminUser');
    
    // console.log('🔍 Admin Auth Context - useEffect Debug:');
    // console.log('  - Token exists in localStorage:', !!token);
    // console.log('  - Token value:', token ? token.substring(0, 20) + '...' : 'null');
    // console.log('  - User data exists:', !!userData);
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // console.log('  - Parsed user:', parsedUser);
        
        // Check if user has admin role (support both old and new role structure)
        const hasAdminRole = parsedUser.role === 'admin' || 
                            (parsedUser.activeRoles && parsedUser.activeRoles.includes('admin')) ||
                            (parsedUser.roles && parsedUser.roles.some((r: any) => r.role === 'admin' && r.isActive));
        
        // console.log('  - Has admin role:', hasAdminRole);
        
        if (hasAdminRole) {
          setUser(parsedUser);
          // console.log('✅ User set successfully');
        } else {
          // console.log('❌ User does not have admin role, clearing storage');
          localStorage.removeItem('adminAuthToken');
          localStorage.removeItem('adminUser');
        }
      } catch (error) {
        // console.log('❌ Error parsing user data:', error);
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('adminUser');
      }
    } else {
      console.log('❌ Missing token or user data');
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string, requestedRole?: string, recaptchaToken?: string) => {
    try {
      setLoading(true);
      // console.log('🔍 Admin Login Debug:');
      // console.log('  - Email:', email);
      // console.log('  - Requested Role:', requestedRole || 'admin');
      
      const response = await adminAuthAPI.login({ 
        email, 
        password, 
        requestedRole: requestedRole || 'admin',
        recaptchaToken 
      });
      
      // console.log('📨 Login Response:');
      // console.log('  - Status:', response.status);
      // console.log('  - Response Data:', response.data);
      // console.log('  - Token exists:', !!response.data.token);
      
      const { token, user } = response.data;
      
      localStorage.setItem('adminAuthToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
      setUser(user);
      
      // console.log('✅ Admin login successful');
      // console.log('  - User stored:', user);
      
      toast.success('Login successful!');
    } catch (error: any) {
      console.error('Admin login error:', error);
      
      // Enhanced error handling for production
      let errorMessage = 'Login failed';
      
      if (error.response?.status === 206) {
        // Don't show error toast for role selection required
        throw error;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message === 'Network Error' || !error.response) {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many attempts. Please wait and try again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Admin privileges required.';
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    setUser(null);
    toast.info('Admin logged out successfully');
  };

  const value: AdminAuthContextType = {
    user,
    login,
    logout,
    loading,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};